if (!customElements.get('quantity-tier-selector')) {
  class QuantityTierSelector extends HTMLElement {
    constructor() {
      super();

      this.input = this.querySelector('.qty-tier__input');
      this.tierBtns = this.querySelectorAll('.qty-tier__tier-btn');
      this.minusBtn = this.querySelector('.qty-tier__btn-minus');
      this.plusBtn = this.querySelector('.qty-tier__btn-plus');
      this.pricingEl = this.querySelector('.qty-tier__pricing');
      this.unitPriceEl = this.querySelector('.qty-tier__unit-price-value');
      this.wasPriceEl = this.querySelector('.qty-tier__was-price');
      this.finalPriceEl = this.querySelector('.qty-tier__final-price');

      this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
      this.tiers = (this.dataset.tiers || '6,12,24').split(',').map(t => parseInt(t.trim(), 10));
      this.discountPercents = (this.dataset.discountPercents || '5,5,5')
        .split(',')
        .map(value => parseInt(value.trim(), 10))
        .filter(value => !Number.isNaN(value));
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}} &euro;';

      this.activeTier = null;

      this.bindEvents();
    }

    bindEvents() {
      this.minusBtn.addEventListener('click', () => this.step(-1));
      this.plusBtn.addEventListener('click', () => this.step(1));
      this.input.addEventListener('change', () => this.onManualChange());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.onManualChange();
        }
      });

      this.tierBtns.forEach(btn => {
        btn.addEventListener('click', () => this.selectTier(btn));
      });

      // Listen for variant changes from the theme's variant-picker
      document.addEventListener('on:variant:change', (e) => {
        if (e.detail && e.detail.variant) {
          this.unitPrice = e.detail.variant.price;
          this.updatePricing();
        }
      });
    }

    step(direction) {
      const current = parseInt(this.input.value, 10) || 1;
      const min = parseInt(this.input.min, 10) || 1;
      const next = Math.max(min, current + direction);
      this.input.value = next;
      this.syncTierHighlight(next);
      this.updatePricing();
      this.dispatchChange();
    }

    onManualChange() {
      const min = parseInt(this.input.min, 10) || 1;
      let val = parseInt(this.input.value, 10);
      if (isNaN(val) || val < min) val = min;
      this.input.value = val;
      this.syncTierHighlight(val);
      this.updatePricing();
      this.dispatchChange();
    }

    selectTier(btn) {
      const qty = parseInt(btn.dataset.qty, 10);
      if (this.activeTier === qty) {
        // Deselect — back to 1
        this.activeTier = null;
        this.input.value = 1;
        this.clearTierHighlights();
        this.pricingEl.hidden = true;
      } else {
        this.activeTier = qty;
        this.input.value = qty;
        this.updateFillLevels(qty);
        this.updatePricing();
      }
      this.dispatchChange();
    }

    syncTierHighlight(qty) {
      const match = this.tiers.includes(qty);
      if (match) {
        this.activeTier = qty;
      } else {
        this.activeTier = null;
      }
      this.updateFillLevels(qty);
    }

    highlightTier(qty) {
      this.updateFillLevels(qty);
    }

    clearTierHighlights() {
      this.tierBtns.forEach(btn => {
        btn.classList.remove('is-active', 'is-filling');
        const fill = btn.querySelector('.qty-tier__tier-fill');
        if (fill) fill.style.clipPath = 'inset(100% 0 0 0)';
      });
    }

    updateFillLevels(qty) {
      let prevTier = 0;
      this.tierBtns.forEach(btn => {
        const tierQty = parseInt(btn.dataset.qty, 10);
        const fill = btn.querySelector('.qty-tier__tier-fill');
        if (!fill) return;

        let ratio = 0;
        if (qty >= tierQty) {
          ratio = 1;
        } else if (qty > prevTier) {
          ratio = (qty - prevTier) / (tierQty - prevTier);
        }

        const fillRatio = Math.min(1, Math.max(0, ratio));
        fill.style.clipPath = 'inset(' + ((1 - fillRatio) * 100) + '% 0 0 0)';
        btn.classList.toggle('is-active', ratio >= 1);
        btn.classList.toggle('is-filling', ratio > 0 && ratio < 1);

        prevTier = tierQty;
      });
    }

    updatePricing() {
      // Find the highest tier reached by current quantity
      const qty = parseInt(this.input.value, 10) || 1;
      let reachedTierIndex = null;
      for (let i = this.tiers.length - 1; i >= 0; i--) {
        if (qty >= this.tiers[i]) {
          reachedTierIndex = i;
          break;
        }
      }

      if (reachedTierIndex === null) {
        this.pricingEl.hidden = true;
        return;
      }

      const fallbackDiscount = this.discountPercents[this.discountPercents.length - 1] || 5;
      const discountPercent = this.discountPercents[reachedTierIndex] ?? fallbackDiscount;
      const discountMultiplier = (100 - discountPercent) / 100;
      const discountedUnit = Math.round(this.unitPrice * discountMultiplier);
      const totalBefore = this.unitPrice * qty;
      const totalAfter = discountedUnit * qty;

      this.unitPriceEl.textContent = this.formatMoney(discountedUnit);
      this.wasPriceEl.textContent = this.formatMoney(totalBefore);
      this.finalPriceEl.textContent = this.formatMoney(totalAfter);

      this.pricingEl.hidden = false;
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      const amountNoDecimals = Math.floor(cents / 100).toString();
      const amountWithPeriod = (cents / 100).toFixed(2);
      const amountNoDecimalsWithComma = amountNoDecimals.replace(/(\d)(?=(\d{3})+$)/g, '$1.');

      return this.moneyFormat
        .replace('{{amount_with_comma_separator}}', amount)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount_with_apostrophe_separator}}', amountWithPeriod)
        .replace('{{amount}}', amountWithPeriod)
        .replace('{{amount}}', amountWithPeriod);
    }

    dispatchChange() {
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  customElements.define('quantity-tier-selector', QuantityTierSelector);
}
